import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getComandas } from "../helpers/rutaComandas";
import "../css/dashboardRentabilidad.css";
import "../css/dashboardViajes.css";

const numero = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

const formatoNumero = (valor) =>
  numero(valor).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const dinero = (valor) =>
  numero(valor).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
  });

const texto = (valor) => String(valor ?? "").trim();

const normalizar = (valor) =>
  texto(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const idRelacion = (valor) =>
  texto(valor && typeof valor === "object" ? valor._id : valor);

// Conserva el día informado por la comanda.
const dia = (valor) => {
  if (!valor) return "";

  const s = texto(valor);
  const coincidencia = s.match(/^(\d{4}-\d{2}-\d{2})/);

  if (coincidencia) return coincidencia[1];

  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return "";

  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
};

const fecha = (valor) => {
  const s = dia(valor);
  return s ? s.split("-").reverse().join("/") : "—";
};

const filtrosIniciales = () => {
  const d = new Date();
  const hasta = dia(d);
    return {
      nroviaje: "",
      desde: hasta.slice(0, 7) + "-01",
      hasta,
      choferId: "",
      transporteId: "",
      clienteFacturaId: "",
      estado: "",
      clienteCargaId: "",
      clienteDescargaId: "",
    };
};

const columnas = [
  { titulo: "N.º viaje", campo: "nroviaje", ancho: 110, fija: "dv-viaje" },
  { titulo: "Fecha", campo: "fecha", ancho: 110, fija: "dv-fecha", tipo: "fecha" },
  { titulo: "Chofer", campo: "chofer", ancho: 220 },
  { titulo: "Transporte", campo: "transporte", ancho: 200 },
  { titulo: "Cliente factura", campo: "clienteFactura", ancho: 220 },
  { titulo: "Factura", campo: "factura", ancho: 180 },
  { titulo: "Cliente carga", campo: "clienteCarga", ancho: 220 },
  { titulo: "Cliente descarga", campo: "clienteDescarga", ancho: 220 },
  { titulo: "Estado", campo: "estado", ancho: 130, tipo: "estado" },
  { titulo: "Origen", campo: "origen", ancho: 190 },
  { titulo: "Destino", campo: "destino", ancho: 190 },
  { titulo: "Camión", campo: "camion", ancho: 190 },
  { titulo: "Kg salida", campo: "kgsal", ancho: 140, tipo: "numero" },
  { titulo: "Kg llegada", campo: "kglle", ancho: 140, tipo: "numero" },
  { titulo: "Diferencia", campo: "dif", ancho: 140, tipo: "numero" },
  { titulo: "Pactado", campo: "pactado", ancho: 170, tipo: "dinero" },
  { titulo: "Pactado tercero", campo: "pactadoter", ancho: 170, tipo: "dinero" },
  { titulo: "Comisión", campo: "comision", ancho: 160, tipo: "dinero" },
];

const camposTotales = [
  "kgsal", "kglle", "dif", "pactado", "pactadoter", "comision",
];

const anchoTabla = columnas.reduce((total, c) => total + c.ancho, 0);

function estadoClave(valor) {
  const s = texto(valor).toUpperCase().replace(/\s+/g, " ");

  if (["PAGA", "PAGADO"].includes(s)) return "PAGADO";
  if (["ACOBRAR", "A COBRAR"].includes(s)) return "A COBRAR";

  return s;
}

function Estado({ valor }) {
  const estado = estadoClave(valor);

  const clase =
    estado === "PAGADO"
      ? "dv-pagado"
      : estado === "A COBRAR"
      ? "dv-cobrar"
      : "dv-otro";

  return (
    <span className={`dv-estado ${clase}`}>
      {estado || "—"}
    </span>
  );
}

function Kpi({ titulo, valor, nota, color = "#2563eb" }) {
  return (
    <div className="dr-kpi" style={{ "--dr-accent": color }}>
      <div className="dr-kpi-label">{titulo}</div>
      <div className="dr-kpi-value">{valor}</div>
      <div className="dr-muted">{nota}</div>
    </div>
  );
}

function crearOpciones(datos, campoId, campoNombre) {
  const opciones = new Map();

  datos.forEach((f) => {
    if (f[campoId] && !opciones.has(f[campoId])) {
      opciones.set(f[campoId], {
        id: f[campoId],
        nombre: f[campoNombre] || "Sin nombre",
      });
    }
  });

  return [...opciones.values()].sort((a, b) =>
    a.nombre.localeCompare(b.nombre, "es")
  );
}

function mostrar(fila, columna) {
  const valor = fila[columna.campo];

  if (columna.tipo === "fecha") return fecha(valor);

  if (columna.tipo === "numero") {
    return valor == null || valor === "" ? "—" : formatoNumero(valor);
  }

  if (columna.tipo === "dinero") {
    return valor == null || valor === "" ? "—" : dinero(valor);
  }

  if (columna.tipo === "estado") return <Estado valor={valor} />;

  return texto(valor) || "—";
}

function errorTexto(error) {
  if (typeof error === "string") return error;
  return error?.message || error?.msg || "No se pudieron cargar los viajes.";
}

export default function DashboardViajes() {
  const [comandas, setComandas] = useState([]);
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [aplicados, setAplicados] = useState(filtrosIniciales);
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let activo = true;

    async function cargar() {
      setCargando(true);
      setError("");
      setComandas([]);

      try {
        const respuesta = await getComandas();

        if (!respuesta?.ok) {
          throw new Error(errorTexto(respuesta?.error || respuesta?.msg));
        }

        if (!Array.isArray(respuesta.comandas)) {
          throw new Error("La respuesta no contiene un listado de comandas válido.");
        }

        if (activo) setComandas(respuesta.comandas);
      } catch (e) {
        if (activo) setError(errorTexto(e));
      } finally {
        if (activo) setCargando(false);
      }
    }

    cargar();
    return () => { activo = false; };
  }, [revision]);

  const datos = useMemo(() => comandas.map((c) => ({
    ...c,
    choferId: idRelacion(c.codcho),
    transporteId: idRelacion(c.codtra),
    clienteFacturaId: idRelacion(c.codclif),
    clienteCargaId: idRelacion(c.codclic),
    clienteDescargaId: idRelacion(c.codclid),
    chofer: texto(c.codcho?.nombre),
    transporte: texto(c.codtra?.transporte),
    clienteFactura: texto(c.codclif?.razonsocial),
    clienteCarga: texto(c.codclic?.razonsocial),
    clienteDescarga: texto(c.codclid?.razonsocial),
    origen: texto(c.codori?.destino),
    destino: texto(c.coddes?.destino),
    camion: texto(c.codcam?.camion),
  })), [comandas]);

  const choferes = useMemo(
    () => crearOpciones(datos, "choferId", "chofer"),
    [datos]
  );

  const transportes = useMemo(
    () => crearOpciones(datos, "transporteId", "transporte"),
    [datos]
  );

    const clientesFactura = useMemo(
    () => crearOpciones(datos, "clienteFacturaId", "clienteFactura"),
    [datos]
  );

  const clientesCarga = useMemo(
    () => crearOpciones(datos, "clienteCargaId", "clienteCarga"),
    [datos]
  );

  const clientesDescarga = useMemo(
    () => crearOpciones(datos, "clienteDescargaId", "clienteDescarga"),
    [datos]
  );

  const estados = useMemo(
    () => [...new Set(datos.map((f) => estadoClave(f.estado)))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "es")),
    [datos]
  );

  const filas = useMemo(() => {
    const nro = normalizar(aplicados.nroviaje);
    const buscar = normalizar(busqueda);

    return datos.filter((f) => {
      const fechaFila = dia(f.fecha);

      // Coincidencia exacta: 1874 no incluye automáticamente 1874,1.
      if (nro && normalizar(f.nroviaje) !== nro) return false;

      if (aplicados.desde && (!fechaFila || fechaFila < aplicados.desde)) {
        return false;
      }

      if (aplicados.hasta && (!fechaFila || fechaFila > aplicados.hasta)) {
        return false;
      }

      if (aplicados.choferId && f.choferId !== aplicados.choferId) return false;

      if (
        aplicados.transporteId &&
        f.transporteId !== aplicados.transporteId
      ) return false;

            if (
        aplicados.clienteFacturaId &&
        f.clienteFacturaId !== aplicados.clienteFacturaId
      ) return false;

      if (
        aplicados.estado &&
        estadoClave(f.estado) !== aplicados.estado
      ) return false;

      if (
        aplicados.clienteCargaId &&
        f.clienteCargaId !== aplicados.clienteCargaId
      ) return false;

      if (
        aplicados.clienteDescargaId &&
        f.clienteDescargaId !== aplicados.clienteDescargaId
      ) return false;

      if (buscar) {
        const contenido = columnas.map((c) =>
          c.tipo === "fecha" ? fecha(f[c.campo]) : f[c.campo]
        ).join(" ");

        if (!normalizar(contenido).includes(buscar)) return false;
      }

      return true;
    }).sort((a, b) =>
      dia(b.fecha).localeCompare(dia(a.fecha)) ||
      texto(b.nroviaje).localeCompare(texto(a.nroviaje), "es", { numeric: true })
    );
  }, [datos, aplicados, busqueda]);

  const totales = useMemo(() => {
    const resultado = Object.fromEntries(camposTotales.map((k) => [k, 0]));

    filas.forEach((f) => {
      camposTotales.forEach((k) => { resultado[k] += numero(f[k]); });
    });

    return resultado;
  }, [filas]);

  const cantidadViajes = useMemo(() => {
    const viajes = new Set();

    filas.forEach((f) => {
      const nro = texto(f.nroviaje);

      if (nro) {
        viajes.add(JSON.stringify([f.transporteId, nro]));
      }
    });

    return viajes.size;
  }, [filas]);

  const cantidadesEstado = useMemo(() => {
    const resultado = { aCobrar: 0, pagado: 0 };

    filas.forEach((f) => {
      const estado = estadoClave(f.estado);

      if (estado === "A COBRAR") resultado.aCobrar++;
      if (estado === "PAGADO") resultado.pagado++;
    });

    return resultado;
  }, [filas]);

  const cambiar = (campo, valor) => {
    setFiltros((actual) => ({
      ...actual,
      [campo]: valor,
      // Al ingresar un viaje se quita el período para buscarlo en todo el listado.
      ...(campo === "nroviaje" && texto(valor)
        ? { desde: "", hasta: "" }
        : {}),
    }));
  };

  const buscar = (e) => {
    e.preventDefault();

    if (
      filtros.desde &&
      filtros.hasta &&
      filtros.desde > filtros.hasta
    ) return;

    setAplicados({ ...filtros });
    setBusqueda("");
  };

  const restablecer = () => {
    const nuevos = filtrosIniciales();
    setFiltros(nuevos);
    setAplicados(nuevos);
    setBusqueda("");
  };

  const periodoInvalido = Boolean(
    filtros.desde && filtros.hasta && filtros.desde > filtros.hasta
  );

  const cambiosPendientes = JSON.stringify(filtros) !== JSON.stringify(aplicados);

  return (
    <main className="danlu-rentabilidad danlu-viajes">
      <div className="dr-heading">
        <div>
          <h1>Dashboard de viajes</h1>
          <div className="dr-muted">
            Consulta de viajes, cargas e importes registrados en comandas
          </div>
        </div>

        <Link to="/HomeImpresion" className="btn btn-outline-secondary btn-sm">
          Volver a informes
        </Link>
      </div>

      <form className="dr-panel" onSubmit={buscar}>
        <div className="dr-filters">
          <div>
            <label htmlFor="dv-nro">N.º de viaje</label>
            <input
              id="dv-nro"
              className="form-control form-control-sm"
              value={filtros.nroviaje}
              onChange={(e) => cambiar("nroviaje", e.target.value)}
              placeholder="Ej.: 1874 o 1874,1"
              disabled={cargando}
            />
          </div>

          <div>
            <label htmlFor="dv-desde">Desde</label>
            <input
              id="dv-desde"
              type="date"
              className="form-control form-control-sm"
              value={filtros.desde}
              onChange={(e) => cambiar("desde", e.target.value)}
              disabled={cargando}
            />
          </div>

          <div>
            <label htmlFor="dv-hasta">Hasta</label>
            <input
              id="dv-hasta"
              type="date"
              className="form-control form-control-sm"
              value={filtros.hasta}
              onChange={(e) => cambiar("hasta", e.target.value)}
              disabled={cargando}
            />
          </div>

          <div>
            <label htmlFor="dv-chofer">Chofer</label>
            <select
              id="dv-chofer"
              className="form-select form-select-sm"
              value={filtros.choferId}
              onChange={(e) => cambiar("choferId", e.target.value)}
              disabled={cargando}
            >
              <option value="">Todos los choferes</option>
              {choferes.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="dv-transporte">Transporte</label>
            <select
              id="dv-transporte"
              className="form-select form-select-sm"
              value={filtros.transporteId}
              onChange={(e) => cambiar("transporteId", e.target.value)}
              disabled={cargando}
            >
              <option value="">Todos los transportes</option>
              {transportes.map((t) => (
                <option key={t.id} value={t.id}>{t.nombre}</option>
              ))}
            </select>
          </div>

                    <div>
            <label htmlFor="dv-cliente-factura">Cliente factura</label>
            <select
              id="dv-cliente-factura"
              className="form-select form-select-sm"
              value={filtros.clienteFacturaId}
              onChange={(e) => cambiar("clienteFacturaId", e.target.value)}
              disabled={cargando}
            >
              <option value="">Todos los clientes</option>
              {clientesFactura.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="dv-filtro-estado">Estado</label>
            <select
              id="dv-filtro-estado"
              className="form-select form-select-sm"
              value={filtros.estado}
              onChange={(e) => cambiar("estado", e.target.value)}
              disabled={cargando}
            >
              <option value="">Todos los estados</option>
              {estados.map((estado) => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="dv-cliente-carga">Cliente carga</label>
            <select
              id="dv-cliente-carga"
              className="form-select form-select-sm"
              value={filtros.clienteCargaId}
              onChange={(e) => cambiar("clienteCargaId", e.target.value)}
              disabled={cargando}
            >
              <option value="">Todos los clientes</option>
              {clientesCarga.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="dv-cliente-descarga">Cliente descarga</label>
            <select
              id="dv-cliente-descarga"
              className="form-select form-select-sm"
              value={filtros.clienteDescargaId}
              onChange={(e) => cambiar("clienteDescargaId", e.target.value)}
              disabled={cargando}
            >
              <option value="">Todos los clientes</option>
              {clientesDescarga.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>

          <div className="dr-actions dv-acciones">
            <button
              className="btn btn-primary btn-sm"
              type="submit"
              disabled={cargando || periodoInvalido || Boolean(error)}
            >
              Buscar
            </button>

            <button
              className="btn btn-outline-secondary btn-sm"
              type="button"
              onClick={restablecer}
              disabled={cargando}
            >
              Restablecer
            </button>

            <button
              className="btn btn-outline-primary btn-sm"
              type="button"
              onClick={() => setRevision((r) => r + 1)}
              disabled={cargando}
            >
              Actualizar datos
            </button>
          </div>
        </div>

        <p className="dr-muted mt-3 mb-0">
          Las fechas son opcionales. Al ingresar un número de viaje se limpia
          el período; podés volver a completarlo para combinar los filtros.
        </p>

        {periodoInvalido && (
          <div className="text-danger mt-2" role="alert">
            Desde debe ser anterior o igual a Hasta.
          </div>
        )}

        {cambiosPendientes && !periodoInvalido && (
          <div className="text-primary mt-2">
            Presioná Buscar para aplicar los cambios.
          </div>
        )}
      </form>

      {cargando && (
        <div className="dr-panel dr-empty" role="status">
          <span className="spinner-border spinner-border-sm me-2" />
          Cargando viajes…
        </div>
      )}

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      {!cargando && !error && (
        <>
          <div className="dr-toolbar">
            <div>
              <strong>
                {aplicados.nroviaje
                  ? `Viaje ${aplicados.nroviaje} · `
                  : ""}
                {aplicados.desde || aplicados.hasta
                  ? `${aplicados.desde ? fecha(aplicados.desde) : "Sin inicio"} al ${
                      aplicados.hasta ? fecha(aplicados.hasta) : "Sin fin"
                    }`
                  : "Todas las fechas"}
              </strong>

              <div className="dr-muted">
                Una fila por comanda. Tarjetas y totales corresponden a las
                filas visibles, incluida la búsqueda de texto.
              </div>
            </div>

            <span className="dr-type">
              {filas.length} comandas de {datos.length} cargadas
            </span>
          </div>

          <div className="dr-kpis">
            <Kpi
              titulo="Viajes distintos"
              valor={cantidadViajes}
              nota={`${filas.length} comandas · por número y transporte`}
            />

            <Kpi
              titulo="A cobrar"
              valor={cantidadesEstado.aCobrar}
              nota="Cantidad de comandas visibles"
              color="#c23838"
            />

            <Kpi
              titulo="Pagado"
              valor={cantidadesEstado.pagado}
              nota="Cantidad de comandas visibles"
              color="#087e57"
            />

            <Kpi
              titulo="Kg salida"
              valor={formatoNumero(totales.kgsal)}
              nota="Total de kilos de salida"
              color="#0c8da5"
            />

            <Kpi
              titulo="Kg llegada"
              valor={formatoNumero(totales.kglle)}
              nota="Total de kilos de llegada"
              color="#2563eb"
            />

            <Kpi
              titulo="Diferencia kg"
              valor={formatoNumero(totales.dif)}
              nota="Suma de las diferencias registradas"
              color="#b7791f"
            />

            <Kpi
              titulo="Pactado"
              valor={dinero(totales.pactado)}
              nota="Importe registrado en comandas"
              color="#087e57"
            />

            <Kpi
              titulo="Pactado tercero"
              valor={dinero(totales.pactadoter)}
              nota="Importe registrado para terceros"
              color="#c23838"
            />

            <Kpi
              titulo="Comisión"
              valor={dinero(totales.comision)}
              nota="Comisión registrada"
              color="#7c4acf"
            />
          </div>

          <section className="dr-panel">
            <div className="dr-toolbar">
              <div>
                <h2 className="mb-1">Detalle de viajes</h2>
                <div className="dr-muted">
                  N.º de viaje y fecha permanecen fijos al desplazar la tabla.
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="dv-busqueda">
                Buscar dentro del resultado
              </label>
              <input
                id="dv-busqueda"
                className="form-control form-control-sm"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Cliente, factura, origen, destino, camión, estado…"
              />
            </div>

            <div
              className="dv-scroll"
              role="region"
              aria-label="Detalle de viajes con desplazamiento horizontal"
              tabIndex={0}
            >
              <table
                className="dv-table"
                style={{ minWidth: anchoTabla }}
              >
                <colgroup>
                  {columnas.map((c) => (
                    <col key={c.campo} style={{ width: c.ancho }} />
                  ))}
                </colgroup>

                <thead>
                  <tr>
                    {columnas.map((c) => (
                      <th
                        key={c.campo}
                        scope="col"
                        className={[
                          c.fija || "",
                          ["numero", "dinero"].includes(c.tipo) ? "dv-num" : "",
                        ].join(" ")}
                      >
                        {c.titulo}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filas.map((f) => (
                    <tr key={f._id}>
                      {columnas.map((c) => (
                        <td
                          key={c.campo}
                          className={[
                            c.fija || "",
                            ["numero", "dinero"].includes(c.tipo) ? "dv-num" : "",
                            ["pactadoter", "comision"].includes(c.campo)
                              ? "dv-importe-tercero"
                              : "",
                          ].join(" ")}
                        >
                          {mostrar(f, c)}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {!filas.length && (
                    <tr>
                      <td colSpan={columnas.length}>
                        No hay comandas que coincidan con los filtros.
                      </td>
                    </tr>
                  )}
                </tbody>

                <tfoot>
                  <tr>
                    {columnas.map((c, i) => (
                      <td
                        key={c.campo}
                        className={[
                          c.fija || "",
                          ["numero", "dinero"].includes(c.tipo) ? "dv-num" : "",
                        ].join(" ")}
                      >
                        {i === 0
                          ? "TOTAL"
                          : i === 1
                          ? `${filas.length} filas`
                          : camposTotales.includes(c.campo)
                          ? c.tipo === "dinero"
                            ? dinero(totales[c.campo])
                            : formatoNumero(totales[c.campo])
                          : ""}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  );
}